import { Board } from '~/components/Shared/Layout/Board/Board';
import AssetsTable from './AssetsTable/AssetsTable';
import AddIcon from '~/assets/svg/newSvg/actions/add.svg?react';
import { ChangeEvent, useState } from 'react';
import styles from './AssetsBoard.module.scss';
import { InputBar, Pagination } from '~/ui';
import { Action } from '~/ui/Actions';
import { useNavigate } from 'react-router-dom';
import { IAsset } from '../Assets';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { getPaginate } from '~/utils/getPaginate';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';

interface IAssetsBoardProps {
  assets: { data: IAsset[]; last_page: number };
  isAssetsLoading: boolean;
  setPage: (value: number) => void;
}

interface SelectedPage {
  selected: number;
}

const AssetsBoard: React.FC<IAssetsBoardProps> = ({
  assets,
  isAssetsLoading,
  setPage,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { t } = useTranslation();
  const [changetAsset, setChangetAsset] = useState({
    assetName: '',
    isEnabled: false,
  });
  const client = useQueryClient();
  const { paginate } = getPaginate();

  const filteredAssets = assets?.data.filter(asset => {
    const searchTermLowered = searchTerm.toLowerCase();
    if (
      asset?.name.toLowerCase().includes(searchTermLowered) ||
      asset?.inn.toLowerCase().includes(searchTermLowered)
    ) {
      return asset;
    }
  });

  const { mutateAsync: assetChecked } = useMutation(
    async ({ assetId, value }: { assetId: number; value: boolean }) => {
      const res = await instance.post(
        `admin/set-enabled-asset-filter/${assetId}`,
        {
          enabled: value,
        }
      );
      return res;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['assets']);
        client.invalidateQueries(['notifications-count']);
        const status = changetAsset.isEnabled
          ? t('successfully_enabled')
          : t('successfully_disabled');
        toastSuccess(
          `${t('asset')} ${changetAsset.assetName} ${t(status).toLowerCase()}`
        );
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Board>
      <div className={styles.assetsTableOptions}>
        <InputBar
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
          }}
          value={searchTerm}
          placeholder={t('search_by_tin_name')}
          classNameInput={styles.assetsTableInput}
          label={`${t('search')}:`}
          star={false}
          classNameInputWrapper={styles.assetsTableInputWrapper}
        />
        <Action
          variant="success"
          onClick={() => {
            navigate('/assets/create');
          }}
        >
          <AddIcon /> {t('create_new_asset')}
        </Action>
      </div>
      <PageTransition page={paginate}>
        <AssetsTable
          assets={filteredAssets || []}
          isFiltered={!!searchTerm}
          assetChecked={(assetId, value) => assetChecked({ assetId, value })}
          setChangetAsset={(assetName, isEnabled) =>
            setChangetAsset({ assetName, isEnabled })
          }
          isLoading={isAssetsLoading}
        />
      </PageTransition>
      <Pagination
        forcePage={paginate}
        pageCount={assets?.last_page}
        onPageChange={(selectedPage: SelectedPage) => {
          setPage(selectedPage.selected + 1);
        }}
      />
    </Board>
  );
};

export default AssetsBoard;
